import { FRM_FILTERS_UI_V2_PANEL_CLASS, type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';

import { isNil } from 'lodash';

const SELECTED_ITEMS_LABEL_PLACEHOLDER = /{[0-9]+}/g;

export interface FilterSelectFloatLabelParams {
  isFloatLabel: boolean | null;
  filtersUiVersion: FiltersUiVersion;
  label?: string | null;
}

export interface FilterSelectPlaceholderParams {
  usesFloatLabel: boolean;
  label?: string | null;
  placeholder?: string | null;
}

export interface FilterSelectChipModeParams {
  filtersUiVersion: FiltersUiVersion;
  usesFloatLabel: boolean;
  label?: string | null;
}

export interface FilterSelectMaxLabelsParams {
  isFilterChipMode: boolean;
  maxSelectedLabels: number;
}

export interface FilterSelectChipOrCommaParams {
  display: string;
  selectedCount: number;
  maxLabels: number;
  /** Для tree-select: в single режиме всегда исходный display. */
  selectionMode?: 'single' | 'multiple' | 'checkbox' | null;
}

export interface FilterSelectAutosizeMeasureParams {
  selectedCount: number;
  maxLabels: number;
  selectedItemsLabel: string;
  isChipDisplay: boolean;
  singleItemLabel?: string | null;
}

/**
 * Общая UI-политика chip/float для `crm-multi-select` и `crm-tree-select` в фильтрах V2.
 */
export class FilterSelectChipUiHelper {
  public static usesFloatLabel(params: FilterSelectFloatLabelParams): boolean {
    if (params.isFloatLabel === true) {
      return true;
    }
    if (params.isFloatLabel === false) {
      return false;
    }
    if (params.filtersUiVersion === 'v2' && !!params.label) {
      return true;
    }
    return params.filtersUiVersion !== 'v2';
  }

  public static inputPlaceholder(params: FilterSelectPlaceholderParams): string {
    if (params.usesFloatLabel && params.label) {
      return '';
    }
    if (params.label) {
      return params.label;
    }
    return params.placeholder ?? '\u00A0';
  }

  public static mergePanelStyleClass(
    panelStyleClass: string | null | undefined,
    filtersUiVersion: FiltersUiVersion
  ): string {
    const base = panelStyleClass?.trim() ?? '';
    const v2 = filtersUiVersion === 'v2' ? FRM_FILTERS_UI_V2_PANEL_CLASS : '';
    return [base, v2].filter(Boolean).join(' ');
  }

  public static isFilterChipMode(params: FilterSelectChipModeParams): boolean {
    return params.filtersUiVersion === 'v2' && params.usesFloatLabel && !!params.label;
  }

  /**
   * В filter-режиме не коллапсируем в «Выбрано: N» — показываем все chip-значения.
   */
  public static effectiveMaxSelectedLabels(params: FilterSelectMaxLabelsParams): number {
    if (params.isFilterChipMode) {
      return Number.MAX_SAFE_INTEGER;
    }
    return params.maxSelectedLabels;
  }

  public static chipOrCommaDisplay(params: FilterSelectChipOrCommaParams): 'comma' | 'chip' | string {
    if (params.selectionMode === 'single') {
      return params.display;
    }
    if (params.display === 'chip') {
      return params.selectedCount > params.maxLabels ? 'comma' : 'chip';
    }
    return params.display;
  }

  public static formatSelectedItemsLabel(template: string, count: number): string {
    return template.replace(SELECTED_ITEMS_LABEL_PLACEHOLDER, count.toString());
  }

  public static resolveChipAutosizeMeasureText(params: FilterSelectAutosizeMeasureParams): string | null {
    if (params.selectedCount > params.maxLabels) {
      return FilterSelectChipUiHelper.formatSelectedItemsLabel(params.selectedItemsLabel, params.selectedCount);
    }
    if (params.selectedCount === 1 && params.isChipDisplay) {
      return isNil(params.singleItemLabel) ? null : params.singleItemLabel;
    }
    return null;
  }
}
